import { tr } from '@/lib/i18n-core';
import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { describeError } from '@/lib/auth';
import { newIdempotencyKey } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Textarea } from '@/components/ui/primitives';
import { REASON_MIN_LENGTH, reasonProblem } from './payables-view';

export function ReasonDialog({
  title,
  description,
  confirmLabel,
  keepLabel,
  consequence,
  run,
  onDone,
  onClose,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  keepLabel: string;
  consequence?: ReactNode;
  run: (reason: string, idempotencyKey: string) => Promise<unknown>;
  onDone: () => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  const submit = useMutation({
    mutationFn: () => run(reason.trim(), idempotencyKey),
    onSuccess: onDone,
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submit.isPending}>
            {keepLabel}
          </Button>
          <Button
            variant="destructive"
            loading={submit.isPending}
            disabled={reasonProblem(reason) !== null}
            onClick={() => {
              setError(null);
              submit.mutate();
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {consequence}
        <Field label={tr('pharmacy', 'reason')} required hint={tr('pharmacy', 'at_least_x_characters_kept_with_the_record', { REASON_MIN_LENGTH: REASON_MIN_LENGTH })}>
          <Textarea rows={3} maxLength={300} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
